import { useMemo } from 'react'

import { useSyncStatusFilter } from 'modules/integrations/public'

import {
  approvedAtFilter,
  creationTypeFilter,
  documentTypeFilter,
  groupedFilter,
  internalReferenceFilter,
  pendingApprovalFilter,
  reimbursableFilter,
  reportDateFilter,
  spendingAlertTypeFilter,
  statusFilter,
  useCategoryFilter,
  useCurrencyFilter,
  useEmployeeIdsFilter,
  useLegalEntityIdsFilter,
  useProjectFilter,
  useReimbursableCurrencyFilter,
  useSubcategoryFilter,
} from './filters'

interface UseExpensesFiltersProps {
  companyId: string
  canUsePerDiems: boolean
  isGroupView?: boolean
  recommendationsEnabled?: boolean
  integrationsFrameworkExpensesSyncFeatureAvailable: boolean
}

/**
 * Filter types:
 * - Simple filters: Use factory functions, no async data fetching
 * - Async filters: Fetch data on-demand from GraphQL, cached by Apollo Client
 */
export const useExpensesFilters = ({
  companyId,
  canUsePerDiems,
  isGroupView,
  recommendationsEnabled,
  integrationsFrameworkExpensesSyncFeatureAvailable,
}: UseExpensesFiltersProps) => {
  const syncStatusFilter = useSyncStatusFilter()
  const showsSyncStatusFilter = !isGroupView && integrationsFrameworkExpensesSyncFeatureAvailable
  const projectFilter = useProjectFilter()

  const categoryFilter = useCategoryFilter({ companyId, canUsePerDiems })
  const currencyFilter = useCurrencyFilter()
  const employeeIdsFilter = useEmployeeIdsFilter(companyId)
  const legalEntityIdsFilter = useLegalEntityIdsFilter()
  const reimbursableCurrencyFilter = useReimbursableCurrencyFilter()
  const subcategoryFilter = useSubcategoryFilter(companyId)

  return useMemo(
    () => ({
      spendingAlertType: spendingAlertTypeFilter(recommendationsEnabled), // alerts
      approvedAt: approvedAtFilter(), // approval date
      category: categoryFilter, // category
      reviewRequestAt: reportDateFilter(), // creation date
      currency: currencyFilter, // currency
      documentType: documentTypeFilter(), // document type
      creationType: creationTypeFilter(), // expense type
      ...(!isGroupView ? { grouped: groupedFilter() } : {}), // grouped
      internalReference: internalReferenceFilter(), // internal reference
      employeeIds: employeeIdsFilter, // owner
      legalEntityIds: legalEntityIdsFilter, // owner legal entity
      reimbursable: reimbursableFilter(), // payment
      pendingApproval: pendingApprovalFilter(), // pending approval
      project: projectFilter, // project
      reimbursableCurrency: reimbursableCurrencyFilter, // reimbursable currency
      status: statusFilter(), // status
      subcategory: subcategoryFilter, // subcategory
      ...(showsSyncStatusFilter ? syncStatusFilter : {}),
    }),
    [
      categoryFilter,
      currencyFilter,
      employeeIdsFilter,
      isGroupView,
      legalEntityIdsFilter,
      projectFilter,
      recommendationsEnabled,
      reimbursableCurrencyFilter,
      showsSyncStatusFilter,
      subcategoryFilter,
      syncStatusFilter,
    ]
  )
}
